import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  OfferCheckoutRequest,
  OfferCheckoutResponse,
  OfferCouponRequest,
  OfferCouponResponse,
  OfferWithDetails,
  useOfferFiltersProps,
} from './interfaces';
import { edupassClient } from '@/data/@clients';
import { clientKeys } from '@/data/@clients/client-keys';
import type { ProductOfferSearch } from '@/models/catalog/offer';

export const useCatalogOffer = (id: number) => {
  const { data, isLoading } = useQuery<{ data: OfferWithDetails }>({
    queryKey: [clientKeys.useCatalogOffer, id],
    queryFn: async () => edupassClient.get(`/catalog/offer/${id}`),
    enabled: !!id,
  });

  return { offer: data?.data, isLoading };
};

export const useCatalogOfferCheckout = (id: number) => {
  const queryClient = useQueryClient();
  const { mutate, isPending } = useMutation<
    OfferCheckoutResponse,
    Error,
    OfferCheckoutRequest
  >({
    mutationFn: async (data: OfferCheckoutRequest) =>
      edupassClient.post(`/catalog/offer/${id}/checkout`, data),
    onSuccess: () => {
      queryClient.refetchQueries({
        queryKey: [clientKeys.useCatalogOffer],
      });
      queryClient.refetchQueries({
        queryKey: [clientKeys.useOfferFilters],
      });
      queryClient.refetchQueries({
        queryKey: [clientKeys.useRegistrationShow],
      });
    },
  });

  return { checkout: mutate, isPending };
};

export const useCatalogOfferCoupon = (id: number) => {
  const { mutate, isPending } = useMutation<
    OfferCouponResponse,
    Error,
    OfferCouponRequest
  >({
    mutationFn: async ({ coupon }: OfferCouponRequest) =>
      edupassClient.get(`/catalog/offer/${id}/coupon/${coupon}`),
  });
  return { coupon: mutate, isPending };
};

export const useOfferFilters = ({
  courseId,
  queryParams,
  enabled,
}: useOfferFiltersProps) => {
  const result = useQuery<{ data: ProductOfferSearch }>({
    queryKey: [clientKeys.useOfferFilters, queryParams],
    queryFn: async () => {
      const url = `/catalog/course/${courseId}/offers${queryParams}`;
      return edupassClient.get(url);
    },
    enabled,
    placeholderData: (previousData) => previousData,
  });

  return {
    data: result.data?.data,
    error: result.error,
    isFetching: result.isFetching,
  };
};
